import React from 'react';
import { Activity, MousePointerClick, FileText, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useGA4 } from '../../contexts/GA4Context';
import TimeRangeSelector from '../../components/analytics/TimeRangeSelector';
import MetricCard from '../../components/analytics/MetricCard';
import DataTable from '../../components/analytics/DataTable';
import { formatNumber, formatPercentage, formatDuration } from '../../utils/analytics-helpers';
import './BehaviorAnalytics.css';

const BehaviorAnalytics = () => {
  const { loading, error, metrics, pages } = useGA4();

  // Prepare top pages data for chart
  const topPagesData = pages?.slice(0, 10).map(p => ({
    page: p.pagePath?.replace(/^\//, '') || 'Home',
    views: p.pageViews || 0,
    engagement: (p.engagementRate || 0) * 100
  })) || [];

  // Table columns for pages
  const pagesColumns = [
    { key: 'pagePath', label: 'Page Path', sortable: true },
    { key: 'pageViews', label: 'Views', sortable: true, format: (val) => formatNumber(val) },
    { key: 'avgSessionDuration', label: 'Avg Duration', sortable: true, format: (val) => formatDuration(val) },
    { key: 'engagementRate', label: 'Engagement', sortable: true, format: (val) => formatPercentage(val) },
    { key: 'entrances', label: 'Entrances', sortable: true, format: (val) => formatNumber(val) },
    { key: 'exits', label: 'Exits', sortable: true, format: (val) => formatNumber(val) }
  ];

  return (
    <div className="behavior-analytics">
      <div className="behavior-header">
        <div>
          <h2>Behavior Analytics</h2>
          <p className="behavior-subtitle">User engagement, page performance, and event tracking</p>
        </div>
        <TimeRangeSelector />
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading behavior data...</p>
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="metrics-grid">
            <MetricCard
              title="Page Views"
              value={metrics?.screenPageViews || 0}
              previousValue={metrics?.previousScreenPageViews}
              format="number"
              icon={FileText}
              subtitle="Total page views"
            />
            <MetricCard
              title="Engagement Rate"
              value={metrics?.engagementRate || 0}
              previousValue={metrics?.previousEngagementRate}
              format="percentage"
              icon={Activity}
              subtitle="Engaged sessions"
            />
            <MetricCard
              title="Avg Session Duration"
              value={metrics?.avgSessionDuration || 0}
              previousValue={metrics?.previousAvgSessionDuration}
              format="duration"
              icon={Clock}
              subtitle="Time per session"
            />
            <MetricCard
              title="Events"
              value={metrics?.eventCount || 0}
              previousValue={metrics?.previousEventCount}
              format="number"
              icon={MousePointerClick}
              subtitle="Total events"
            />
          </div>

          {/* Top Pages Chart */}
          <div className="chart-container">
            <h3>Top Pages by Views</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={topPagesData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fill: '#6B7280', fontSize: 12 }} />
                <YAxis
                  type="category"
                  dataKey="page"
                  tick={{ fill: '#6B7280', fontSize: 11 }}
                  width={120}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar dataKey="views" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Page Performance Table */}
          <div className="table-container">
            <DataTable
              data={pages || []}
              columns={pagesColumns}
              title="Page Performance Details"
              searchable={true}
              exportable={true}
              pageSize={25}
            />
          </div>

          {/* Engagement Insights */}
          <div className="insights-grid">
            <div className="insight-card">
              <div className="insight-icon" style={{ background: '#DBEAFE', color: '#3B82F6' }}>
                <FileText size={24} />
              </div>
              <div className="insight-content">
                <h4>Most Viewed Page</h4>
                <p className="insight-value">{pages?.[0]?.pagePath || 'N/A'}</p>
                <p className="insight-detail">
                  {formatNumber(pages?.[0]?.pageViews || 0)} views
                </p>
              </div>
            </div>

            <div className="insight-card">
              <div className="insight-icon" style={{ background: '#D1FAE5', color: '#10B981' }}>
                <Activity size={24} />
              </div>
              <div className="insight-content">
                <h4>Highest Engagement</h4>
                <p className="insight-value">
                  {pages?.sort((a, b) => (b.engagementRate || 0) - (a.engagementRate || 0))[0]?.pagePath || 'N/A'}
                </p>
                <p className="insight-detail">
                  {formatPercentage(pages?.sort((a, b) => (b.engagementRate || 0) - (a.engagementRate || 0))[0]?.engagementRate || 0)} engagement
                </p>
              </div>
            </div>

            <div className="insight-card">
              <div className="insight-icon" style={{ background: '#FEF3C7', color: '#F59E0B' }}>
                <Clock size={24} />
              </div>
              <div className="insight-content">
                <h4>Longest Session</h4>
                <p className="insight-value">
                  {pages?.sort((a, b) => (b.avgSessionDuration || 0) - (a.avgSessionDuration || 0))[0]?.pagePath || 'N/A'}
                </p>
                <p className="insight-detail">
                  {formatDuration(pages?.sort((a, b) => (b.avgSessionDuration || 0) - (a.avgSessionDuration || 0))[0]?.avgSessionDuration || 0)} avg
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BehaviorAnalytics;
